'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import NationalitySelect from '@/components/ui/NationalitySelect';
import {
    pageVariants,
    formItemVariants,
    slideFromRightVariants,
    staggerContainerVariants,
} from '@/lib/animations';
import { clearReservationCookies, RESERVATION_CHECKOUT_COOKIE_NAME } from '@/lib/cookies';
import { resolveUrlFromMap } from '@/lib/blog-transformers';
import {
    loadRecaptchaV3Script,
    getRecaptchaV3Token,
    loadRecaptchaScript,
    renderRecaptchaWidget,
    resetRecaptchaWidget,
    getRecaptchaToken,
    loadTurnstileScript,
    renderTurnstileWidget,
    resetTurnstileWidget,
    getTurnstileToken,
} from '@/lib/captcha';
import { useCaptchaConfig } from '@/hooks/useCaptchaConfig';
const RESERVATION_CHECKOUT_COOKIE = RESERVATION_CHECKOUT_COOKIE_NAME;
const RESERVATION_COOKIE_MAX_AGE_DAYS = 7;
const TURKEY_CODE = 'TR';
/** Draft API yanıtı için maksimum bekleme (timeout önleme) */
const DRAFT_FETCH_TIMEOUT_MS = 15000;
/** Sayfa spinner'ı için maksimum bekleme; aşılırsa form gösterilir veya yönlendirme yapılır */
const MAX_LOADING_WAIT_MS = 12000;

interface CheckoutSummary {
    flow?: 'request_first' | 'reservation';
    product_id?: number;
    product_type?: string;
    product_name?: string;
    travel_date?: string;
    check_in_date?: string;
    check_out_date?: string;
    guest_count?: number;
    child_count?: number;
    rooms?: Array<{ adults: number; children: number; childAges?: number[] }>;
    room_prices?: Array<{ roomIndex: number; total_price: number; currency?: { code?: string; symbol?: string } }>;
    total_price?: number;
    coupon_discount_amount?: number;
    currency?: { code?: string; symbol?: string };
    coupon_code?: string;
    special_requests?: string;
    agreements?: Array<{ id: number; accepted: boolean }>;
    language_id?: number;
    draft_id?: number;
    reservation_no?: string;
    /** Rezervasyon formunda alınan reCAPTCHA token (checkout submit'te backend'e gönderilir) */
    recaptcha_token?: string;
    /** request_first: FormResponse için form detayları */
    form_id?: number;
    form_values?: Record<string, unknown>;
    /** Seçilen paket (Gold, Standard vb.); fatura ve voucher’da gösterilir */
    package_name?: string;
    /** Grup turu çıkış tarihi rezervasyonu için period_id (tour_departure_dates kontenjanı için) */
    period_id?: number;
    pricing_period_type?: 'daily';
}

interface ParticipantField {
    type: 'adult' | 'child';
    first_name: string;
    last_name: string;
    national_id: string;
    passport_no: string;
    nationality: string;
    /** Çocuk doğum tarihi (YYYY-MM-DD); rooms verisi ile admin/voucher'da gösterilir */
    birth_date?: string;
    save_for_future?: boolean;
}

interface SavedTraveler {
    id: number;
    first_name: string;
    last_name: string;
    passport_no?: string | null;
    tc_no?: string | null;
    nationality?: string | null;
}

interface ReservationCheckoutProps {
    language?: string;
    pageUrls?: Record<string, string>;
    translations?: Record<string, string>;
}

const motionItemProps = { initial: 'hidden' as const, animate: 'visible' as const };

export default function ReservationCheckout({ language = 'en', pageUrls, translations = {} }: ReservationCheckoutProps) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { getVariable } = useLanguageVariables();
    const { isAuthenticated, customer, isLoading: authLoading } = useCustomerAuth();
    const [summary, setSummary] = useState<CheckoutSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        national_id: '',
        passport_no: '',
        nationality: '',
    });
    const [participantsByRoom, setParticipantsByRoom] = useState<ParticipantField[][]>([]);
    const [savedTravelers, setSavedTravelers] = useState<SavedTraveler[]>([]);
    const formContainerRef = useRef<HTMLDivElement>(null);
    const firstInputRef = useRef<HTMLInputElement>(null);
    /** Saved-travelers sadece pasaport/TC alanına odaklanınca çekilsin (sayfa açılışta 403 önleme) */
    const savedTravelersFetched = useRef(false);
    /** Timeout aşıldıysa spinner yerine içerik göster (sayfa kilitlenmesin) */
    const [loadingTimedOut, setLoadingTimedOut] = useState(false);
    /** request_first form submit için captcha */
    const captchaWidgetIdRef = useRef<number | string | null>(null);
    const { config: captchaConfig, isLoading: captchaConfigLoading } = useCaptchaConfig();
    const isCaptchaRequired = Boolean(captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider);
    /** request_first başarılı submit sonrası (redirect yerine sayfada gösterim) */
    const [requestSubmitSuccess, setRequestSubmitSuccess] = useState(false);
    /** request_no: backend'den dönen talep numarası (REQ-YYYYMMDD-XXXXX) */
    const [requestNo, setRequestNo] = useState<string | null>(null);

    const t = (key: string, fallback = '') => translations[key] ?? getVariable(key, fallback);

    const scrollToFormAndFocus = useCallback(() => {
        formContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => firstInputRef.current?.focus({ preventScroll: false }), 400);
    }, []);

    const buildParticipantSlots = useCallback((rooms: CheckoutSummary['rooms']) => {
        if (!rooms?.length) return [];
        return rooms.map((room) => {
            const adults = room.adults ?? 0;
            const children = room.children ?? 0;
            const slots: ParticipantField[] = [];
            for (let i = 0; i < adults; i++) {
                slots.push({ type: 'adult', first_name: '', last_name: '', national_id: '', passport_no: '', nationality: '', save_for_future: false });
            }
            for (let i = 0; i < children; i++) {
                slots.push({ type: 'child', first_name: '', last_name: '', national_id: '', passport_no: '', nationality: '', birth_date: '', save_for_future: false });
            }
            return slots;
        });
    }, []);

    // URL'den grup turu parametreleri (Tarihler ve Müsaitlik → Rezervasyon Yap ile gelen)
    const urlGroupParams = useMemo(() => {
        if (!searchParams) return null;
        const pid = searchParams.get('product_id');
        const slug = searchParams.get('product_slug');
        const dep = searchParams.get('departure_date');
        const ad = searchParams.get('adults');
        const ch = searchParams.get('children');
        const periodId = searchParams.get('period_id');
        if (!pid || !dep) return null;
        const productId = parseInt(pid, 10);
        if (Number.isNaN(productId)) return null;
        const adults = ad ? Math.max(1, parseInt(ad, 10) || 2) : 2;
        const children = ch ? Math.max(0, parseInt(ch, 10) || 0) : 0;
        return {
            product_id: productId,
            product_slug: slug || null,
            departure_date: dep,
            adults,
            children,
            period_id: periodId ? (parseInt(periodId, 10) || undefined) : undefined,
        };
    }, [searchParams]);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const raw = document.cookie
            .split('; ')
            .find((row) => row.startsWith(`${RESERVATION_CHECKOUT_COOKIE}=`));
        if (!raw) {
            setSummary(null);
            setLoading(false);
            return;
        }
        try {
            const value = decodeURIComponent(raw.split('=').slice(1).join('='));
            const data = JSON.parse(value) as CheckoutSummary;
            setSummary(data);
            if (data?.rooms?.length) {
                setParticipantsByRoom(buildParticipantSlots(data.rooms));
            }
        } catch {
            setSummary(null);
        }
        setLoading(false);
    }, [buildParticipantSlots]);

    // Sayfa yüklenirken auth/draft uzun sürerse spinner en fazla MAX_LOADING_WAIT_MS kalsın; sonra içeriği göster
    useEffect(() => {
        if (!loading && loadingTimedOut) return;
        const t = setTimeout(() => setLoadingTimedOut(true), MAX_LOADING_WAIT_MS);
        return () => clearTimeout(t);
    }, [loading, loadingTimedOut]);

    const persistSummaryToCookie = useCallback((next: CheckoutSummary) => {
        if (typeof document === 'undefined') return;
        const maxAge = RESERVATION_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
        document.cookie = `${RESERVATION_CHECKOUT_COOKIE}=${encodeURIComponent(JSON.stringify(next))}; path=/; max-age=${maxAge}; SameSite=Lax`;
    }, []);

    // Grup turu: URL'de product_id + departure_date varsa ve çerezde özet yoksa, ürünü çekip özet oluştur
    useEffect(() => {
        if (!urlGroupParams || summary != null || loading) return;
        const { product_id: productId, product_slug: productSlug, departure_date: departureDate, adults, children, period_id: periodId } = urlGroupParams;
        const rooms = [{ adults, children, childAges: [] as number[] }];
        const guestCount = adults + children;
        const childCount = children;

        const hydrateFromProduct = (name: string, type: string) => {
            const next: CheckoutSummary = {
                flow: 'reservation',
                product_id: productId,
                product_name: name,
                product_type: type || 'tour',
                travel_date: departureDate,
                check_in_date: departureDate,
                guest_count: guestCount,
                child_count: childCount,
                rooms,
                period_id: periodId,
            };
            persistSummaryToCookie(next);
            setSummary(next);
            setParticipantsByRoom(buildParticipantSlots(rooms));
            setLoading(false);
        };

        if (productSlug && productSlug.trim()) {
            setLoading(true);
            fetch(`/api/products/${encodeURIComponent(productSlug)}?language=${language}`)
                .then((r) => r.json())
                .then((data) => {
                    const product = data?.data ?? data;
                    const name = product?.name ?? '';
                    const type = product?.type ?? product?.product_type ?? 'tour';
                    hydrateFromProduct(name, type);
                })
                .catch(() => {
                    hydrateFromProduct('', 'tour');
                })
                .finally(() => setLoading(false));
        } else {
            hydrateFromProduct('', 'tour');
        }
    }, [urlGroupParams, summary, loading, language, persistSummaryToCookie, buildParticipantSlots]);

    // Draft checkout sayfası açılışında oluşturulmaz; sadece sidebar'dan butonla gelindiğinde (çerezde draft_id) veya form POST edildiğinde backend yeni rezervasyon açar. Sayfa açılır açılmaz API çağrısı yapılmaz.

    // Auth: request_first flow'da login zorunlu değil (misafir talep); reservation flow'da zorunlu
    const isRequestFirstFlow = summary?.flow === 'request_first';
    const shouldShowContactForm = isRequestFirstFlow || !isAuthenticated;
    useEffect(() => {
        if (loading) return;
        if (authLoading && !loadingTimedOut) return;
        if (isRequestFirstFlow) return; // Talep formu: misafir checkout
        if (!isAuthenticated && pathname) {
            const returnPath = encodeURIComponent(pathname);
            const loginPath = pageUrls?.login ?? `/${language}/login`;
            router.replace(`${loginPath}?return=${returnPath}`);
        }
    }, [loading, authLoading, loadingTimedOut, isAuthenticated, pathname, language, router, pageUrls, isRequestFirstFlow]);

    useEffect(() => {
        if (customer && !form.first_name) {
            setForm((prev) => ({
                ...prev,
                first_name: customer.first_name || prev.first_name,
                last_name: customer.last_name || prev.last_name,
                email: customer.email || prev.email,
                phone: customer.phone || prev.phone || '',
            }));
        }
    }, [customer]);

    // request_first: form_values'tan iletişim bilgilerini önceden doldur
    useEffect(() => {
        if (summary?.flow === 'request_first' && summary?.form_values && !form.email) {
            const fv = summary.form_values as Record<string, unknown>;
            setForm((prev) => ({
                ...prev,
                first_name: (fv.name ?? fv.first_name ?? prev.first_name) as string,
                last_name: (fv.surname ?? fv.last_name ?? prev.last_name) as string,
                email: (fv.email ?? prev.email) as string,
                phone: (fv.mobile ?? fv.phone ?? prev.phone) as string,
            }));
        }
    }, [summary?.flow, summary?.form_values]);

    // Captcha widget (v2 / Turnstile) – hem talep hem rezervasyon akışında; taze token için checkout’ta gösteriliyor
    useEffect(() => {
        if (!isCaptchaRequired || !captchaConfig?.site_key || !captchaConfig?.provider) return;
        const provider = captchaConfig.provider;
        const siteKey = captchaConfig.site_key;
        const scriptUrl = captchaConfig.script_url;
        const initCaptcha = async () => {
            try {
                if (provider === 'google_recaptcha_v2') {
                    await loadRecaptchaScript(scriptUrl || undefined);
                    const id = await renderRecaptchaWidget('checkout-request-captcha', siteKey, () => {});
                    captchaWidgetIdRef.current = id;
                } else if (provider === 'cloudflare_turnstile') {
                    await loadTurnstileScript(scriptUrl || undefined);
                    const id = await renderTurnstileWidget('checkout-request-captcha', siteKey, () => {});
                    captchaWidgetIdRef.current = id;
                }
            } catch {
                // ignore
            }
        };
        const t = setTimeout(initCaptcha, 500);
        return () => {
            clearTimeout(t);
            if (captchaWidgetIdRef.current != null && provider === 'google_recaptcha_v2') {
                resetRecaptchaWidget(captchaWidgetIdRef.current as number);
            } else if (captchaWidgetIdRef.current != null && provider === 'cloudflare_turnstile') {
                resetTurnstileWidget(captchaWidgetIdRef.current as string);
            }
        };
    }, [isCaptchaRequired, captchaConfig]);

    // Kayıtlı yolcuları sadece ihtiyaç olduğunda çek (sayfa açılışta GET 403 önlenir; form POST olmadan çalışmaz)
    const loadSavedTravelersOnce = useCallback(() => {
        if (!isAuthenticated || savedTravelersFetched.current) return;
        savedTravelersFetched.current = true;
        fetch('/api/customer/saved-travelers', { credentials: 'include' })
            .then((r) => r.json())
            .then((data) => {
                if (data?.data && Array.isArray(data.data)) {
                    setSavedTravelers(data.data as SavedTraveler[]);
                }
            })
            .catch(() => {
                savedTravelersFetched.current = false;
            });
    }, [isAuthenticated]);

    const buildParticipantsPayload = useCallback(() => {
        const list: Array<{ room_index: number; guest_index: number; type: 'adult' | 'child'; first_name: string; last_name: string; tc_no?: string; passport_no?: string; nationality?: string; birth_date?: string | null; save_for_future?: boolean }> = [];
        participantsByRoom.forEach((travelers, roomIndex) => {
            travelers.forEach((p, guestIndex) => {
                list.push({
                    room_index: roomIndex,
                    guest_index: guestIndex,
                    type: p.type,
                    first_name: p.first_name,
                    last_name: p.last_name,
                    tc_no: p.national_id || undefined,
                    passport_no: p.passport_no || undefined,
                    nationality: p.nationality || undefined,
                    // Çocuk doğum tarihi: backend rooms.guests[].birth_date ve reservation_participants.birth_date olarak kaydedilir
                    birth_date: p.type === 'child' ? (p.birth_date?.trim() || null) : undefined,
                    save_for_future: p.save_for_future || undefined,
                });
            });
        });
        return list;
    }, [participantsByRoom]);

    const fillGuestFromSavedTraveler = useCallback((roomIndex: number, guestIndex: number, passportNo: string, tcNo: string) => {
        const match = savedTravelers.find(
            (st) =>
                (passportNo && st.passport_no && st.passport_no.trim() === passportNo.trim()) ||
                (tcNo && st.tc_no && st.tc_no.trim() === tcNo.trim())
        );
        if (!match) return;
        setParticipantsByRoom((prev) =>
            prev.map((r, ri) =>
                ri === roomIndex
                    ? r.map((p, pi) =>
                          pi === guestIndex
                              ? {
                                    ...p,
                                    first_name: p.first_name || match.first_name || '',
                                    last_name: p.last_name || match.last_name || '',
                                    nationality: p.nationality || match.nationality || '',
                                }
                              : p
                      )
                    : r
            )
        );
    }, [savedTravelers]);

    const showTcForNationality = (nationality: string) => nationality === TURKEY_CODE;

    const participantFieldKey = (roomIndex: number, guestIndex: number, field: string) =>
        `p_${roomIndex}_${guestIndex}_${field}`;

    const validateForm = useCallback((): boolean => {
        const err: Record<string, string> = {};
        const fillRequired = t('reservation.fill_required_fields');
        const firstName = form.first_name?.trim() || customer?.first_name?.trim() || '';
        const lastName = form.last_name?.trim() || customer?.last_name?.trim() || '';
        const email = form.email?.trim() || customer?.email?.trim() || '';
        if (!firstName) err.first_name = fillRequired;
        if (!lastName) err.last_name = fillRequired;
        if (!email) err.email = fillRequired;

        participantsByRoom.forEach((travelers, roomIndex) => {
            travelers.forEach((p, guestIndex) => {
                const key = (field: string) => participantFieldKey(roomIndex, guestIndex, field);
                if (!p.first_name?.trim()) {
                    err[key('first_name')] = t('reservation.participant_name_required');
                }
                if (!p.last_name?.trim()) {
                    err[key('last_name')] = t('reservation.participant_name_required');
                }
                if (!p.nationality?.trim()) {
                    err[key('nationality')] = t('reservation.participant_nationality_required');
                }
                if (showTcForNationality(p.nationality)) {
                    if (!p.national_id?.trim() || p.national_id.length !== 11) {
                        err[key('national_id')] = t('reservation.participant_tc_required');
                    }
                } else if (p.nationality?.trim() && !p.passport_no?.trim()) {
                    err[key('passport_no')] = t('reservation.participant_passport_required');
                }
                if (p.type === 'child' && !p.birth_date?.trim()) {
                    err[key('birth_date')] = t('reservation.participant_birthdate_required');
                }
            });
        });

        setFieldErrors(err);
        setError(Object.keys(err).length > 0 ? fillRequired : null);
        return Object.keys(err).length === 0;
    }, [form.first_name, form.last_name, form.email, getVariable, customer?.first_name, customer?.last_name, customer?.email, participantsByRoom, t]);

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            if (!summary) return;
            if (!validateForm()) {
                scrollToFormAndFocus();
                return;
            }
            const resolvedFirstName = form.first_name.trim() || customer?.first_name?.trim() || '';
            const resolvedLastName = form.last_name.trim() || customer?.last_name?.trim() || '';
            const resolvedEmail = form.email.trim() || customer?.email?.trim() || '';
            setSubmitting(true);
            setError(null);
            setFieldErrors({});
            try {
                // request_first: Talep formu submit (FormResponse oluştur, rezervasyon yok)
                if (summary.flow === 'request_first' && summary.form_id) {
                    let recaptchaToken = '';
                    if (isCaptchaRequired && captchaConfig?.provider && captchaConfig?.site_key) {
                        if (captchaConfig.provider === 'google_recaptcha_v2') {
                            recaptchaToken = getRecaptchaToken(captchaWidgetIdRef.current as number | undefined) || '';
                            if (!recaptchaToken) {
                                setError(getVariable('common.captcha_required', 'Lütfen robot doğrulamayı tamamlayın.'));
                                setSubmitting(false);
                                return;
                            }
                        } else if (captchaConfig.provider === 'google_recaptcha_v3' || captchaConfig.provider === 'google_recaptcha_enterprise') {
                            try {
                                await loadRecaptchaV3Script(
                                    captchaConfig.site_key ?? '',
                                    captchaConfig.provider === 'google_recaptcha_enterprise' ? captchaConfig.script_url ?? undefined : undefined
                                );
                                recaptchaToken = await getRecaptchaV3Token(captchaConfig.site_key ?? '', 'request_form') || '';
                            } catch {
                                recaptchaToken = '';
                            }
                            if (!recaptchaToken) {
                                setError(getVariable('common.captcha_required', 'Lütfen robot doğrulamayı tamamlayın.'));
                                setSubmitting(false);
                                return;
                            }
                        } else if (captchaConfig.provider === 'cloudflare_turnstile') {
                            recaptchaToken = getTurnstileToken(captchaWidgetIdRef.current as string | undefined) || '';
                            if (!recaptchaToken) {
                                setError(getVariable('common.captcha_required', 'Lütfen robot doğrulamayı tamamlayın.'));
                                setSubmitting(false);
                                return;
                            }
                        }
                    }
                    const participants = buildParticipantsPayload();
                    const roomsWithGuests = (summary.rooms ?? []).map((room, roomIndex) => {
                        const roomParticipants = participants.filter((p) => p.room_index === roomIndex);
                        const guests = roomParticipants.map((p) => ({
                            type: p.type,
                            first_name: p.first_name,
                            last_name: p.last_name,
                            birth_date: p.type === 'child' ? (p.birth_date || undefined) : undefined,
                            nationality: p.nationality || undefined,
                            passport_no: p.passport_no || undefined,
                            tc_no: p.tc_no || undefined,
                        }));
                        return {
                            adults: room.adults ?? 0,
                            children: room.children ?? 0,
                            childAges: room.childAges ?? [],
                            guests: guests.length > 0 ? guests : undefined,
                        };
                    });
                    const formPayload: Record<string, unknown> = {
                        ...(summary.form_values || {}),
                        form_id: summary.form_id,
                        product_id: summary.product_id,
                        product_type: summary.product_type || 'tour',
                        name: resolvedFirstName,
                        surname: resolvedLastName,
                        first_name: resolvedFirstName,
                        last_name: resolvedLastName,
                        email: resolvedEmail,
                        mobile: form.phone?.trim() || '',
                        phone: form.phone?.trim() || '',
                        people: String(summary.guest_count ?? 1),
                        guest_count: summary.guest_count ?? 1,
                        travel_date: summary.travel_date || summary.check_in_date,
                        departure_date: summary.travel_date || summary.check_in_date,
                        check_in_date: summary.check_in_date,
                        check_out_date: summary.check_out_date,
                        return_date: summary.check_out_date,
                        rooms: roomsWithGuests,
                        agreements: summary.agreements?.map((a) => ({ id: a.id, accepted: true, required: true })) ?? [],
                    };
                    if (summary.language_id != null) formPayload.language_id = summary.language_id;
                    if (recaptchaToken) formPayload.recaptcha_token = recaptchaToken;
                    const res = await fetch('/api/form/submit', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify(formPayload),
                    });
                    const data = await res.json();
                    if (!res.ok) {
                        const errMsg = data?.message ?? data?.error ?? getVariable('request_form.error_generic', 'Gönderilemedi.');
                        throw new Error(errMsg);
                    }
                    const requestNoFromApi = data?.data?.request_no ?? null;
                    if (requestNoFromApi) setRequestNo(requestNoFromApi);
                    clearReservationCookies();
                    setRequestSubmitSuccess(true);
                    if (captchaConfig?.provider === 'google_recaptcha_v2' && captchaWidgetIdRef.current != null) {
                        resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                    } else if (captchaConfig?.provider === 'cloudflare_turnstile' && captchaWidgetIdRef.current != null) {
                        resetTurnstileWidget(captchaWidgetIdRef.current as string);
                    }
                    return;
                }

                const participants = buildParticipantsPayload();
                // reCAPTCHA v2 token’ları tek kullanımlıktır; her submit’te checkout’taki widget’tan taze token alınmalı
                let recaptchaTokenReservation = '';
                if (isCaptchaRequired && captchaConfig?.provider && captchaConfig?.site_key) {
                    if (captchaConfig.provider === 'google_recaptcha_v2') {
                        recaptchaTokenReservation = getRecaptchaToken(captchaWidgetIdRef.current as number | undefined) || '';
                        if (!recaptchaTokenReservation) {
                            setError(getVariable('common.captcha_required', 'Lütfen robot doğrulamayı tamamlayın.'));
                            setSubmitting(false);
                            return;
                        }
                    } else if (captchaConfig.provider === 'google_recaptcha_v3' || captchaConfig.provider === 'google_recaptcha_enterprise') {
                        try {
                            await loadRecaptchaV3Script(
                                captchaConfig.site_key ?? '',
                                captchaConfig.provider === 'google_recaptcha_enterprise' ? captchaConfig.script_url ?? undefined : undefined
                            );
                            recaptchaTokenReservation = await getRecaptchaV3Token(captchaConfig.site_key ?? '', 'reservation_checkout') || '';
                        } catch {
                            recaptchaTokenReservation = '';
                        }
                        if (!recaptchaTokenReservation) {
                            setError(getVariable('common.captcha_required', 'Lütfen robot doğrulamayı tamamlayın.'));
                            setSubmitting(false);
                            return;
                        }
                    } else if (captchaConfig.provider === 'cloudflare_turnstile') {
                        recaptchaTokenReservation = getTurnstileToken(captchaWidgetIdRef.current as string | undefined) || '';
                        if (!recaptchaTokenReservation) {
                            setError(getVariable('common.captcha_required', 'Lütfen robot doğrulamayı tamamlayın.'));
                            setSubmitting(false);
                            return;
                        }
                    }
                }
                const payload = {
                    product_id: summary.product_id,
                    product_type: summary.product_type || 'tour',
                    guest_count: summary.guest_count ?? 1,
                    child_count: summary.child_count ?? 0,
                    infant_count: 0,
                    travel_date: summary.travel_date || summary.check_in_date,
                    check_in_date: summary.check_in_date,
                    check_out_date: summary.check_out_date,
                    special_requests: summary.special_requests,
                    language_id: summary.language_id,
                    agreements: summary.agreements,
                    draft_id: summary.draft_id ?? undefined,
                    coupon_code: summary.coupon_code ?? undefined,
                    recaptcha_token: (recaptchaTokenReservation || summary.recaptcha_token) ?? undefined,
                    first_name: resolvedFirstName,
                    last_name: resolvedLastName,
                    email: resolvedEmail,
                    phone: form.phone?.trim() || undefined,
                    tc_no: form.nationality === TURKEY_CODE ? (form.national_id?.trim() || undefined) : undefined,
                    passport_no: form.passport_no?.trim() || undefined,
                    nationality: form.nationality || undefined,
                    participants: participants.length > 0 ? participants : undefined,
                    ...(summary.rooms && summary.rooms.length > 0
                        ? {
                              rooms: summary.rooms.map((r) => ({
                                  adults: r.adults ?? 1,
                                  children: r.children ?? 0,
                                  childAges: r.childAges ?? [],
                              })),
                          }
                        : {}),
                    // Fiyat bilgisi: backend fiyat hesaplaması 0 dönerse fatura için fallback (tur ödeme sayfası hatasını önler)
                    ...(summary.total_price != null && summary.total_price > 0 ? { total_price: summary.total_price } : {}),
                    ...(summary.room_prices && summary.room_prices.length > 0 ? { room_prices: summary.room_prices } : {}),
                    ...(summary.package_name ? { package_name: summary.package_name } : {}),
                    ...(summary.period_id != null ? { period_id: summary.period_id } : {}),
                    ...(summary.pricing_period_type === 'daily' ? { pricing_period_type: 'daily' } : {}),
                };
                let res = await fetch('/api/reservations/submit', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(payload),
                });
                let data = await res.json();

                // draft_id ile 404 alındıysa (yarım kalan bulunamadı) draft_id'siz tekrar dene
                if (res.status === 404 && payload.draft_id) {
                    const retryPayload = { ...payload, draft_id: undefined };
                    res = await fetch('/api/reservations/submit', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify(retryPayload),
                    });
                    data = await res.json();
                    if (res.ok) {
                        setSummary((s) => (s ? { ...s, draft_id: undefined } : s));
                        persistSummaryToCookie(summary ? { ...summary, draft_id: undefined } : summary!);
                    }
                }

                if (!res.ok) {
                    if (captchaConfig?.provider === 'google_recaptcha_v2' && captchaWidgetIdRef.current != null) {
                        resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                    } else if (captchaConfig?.provider === 'cloudflare_turnstile' && captchaWidgetIdRef.current != null) {
                        resetTurnstileWidget(captchaWidgetIdRef.current as string);
                    }
                    const msg = data.message || data.errors?.email?.[0] || getVariable('reservation.submit_error', 'Submit failed');
                    throw new Error(msg);
                }
                if (captchaConfig?.provider === 'google_recaptcha_v2' && captchaWidgetIdRef.current != null) {
                    resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                } else if (captchaConfig?.provider === 'cloudflare_turnstile' && captchaWidgetIdRef.current != null) {
                    resetTurnstileWidget(captchaWidgetIdRef.current as string);
                }
                const reservationId = data?.data?.reservation?.id;
                if (reservationId != null) {
                    const nextSummary = summary ? { ...summary, draft_id: reservationId, reservation_no: data?.data?.reservation?.reservation_no ?? summary.reservation_no } : summary;
                    if (nextSummary) {
                        setSummary(nextSummary);
                        persistSummaryToCookie(nextSummary);
                    }
                }
                // Rezervasyon oluşturuldu; yeni rezervasyonda eski veri kalmasın diye çerezleri temizle
                clearReservationCookies();
                if (data?.data?.redirect_url) {
                    window.location.href = data.data.redirect_url;
                    return;
                }
                const paymentSlug = language === 'tr' ? 'rezervasyon/odeme' : 'payment';
                router.push(`/${language}/${paymentSlug}`);
            } catch (err) {
                setError(err instanceof Error ? err.message : t('reservation.submit_error'));
                if (captchaConfig?.provider === 'google_recaptcha_v2' && captchaWidgetIdRef.current != null) {
                    resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                } else if (captchaConfig?.provider === 'cloudflare_turnstile' && captchaWidgetIdRef.current != null) {
                    resetTurnstileWidget(captchaWidgetIdRef.current as string);
                }
            } finally {
                setSubmitting(false);
            }
        },
        [summary, form, language, router, t, buildParticipantsPayload, validateForm, scrollToFormAndFocus, getVariable, persistSummaryToCookie, isCaptchaRequired, captchaConfig, customer?.first_name, customer?.last_name, customer?.email]
    );

    const stillLoading = (loading || authLoading) && !loadingTimedOut;
    if (stillLoading) {
        return (
            <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-alsero-first" />
            </div>
        );
    }

    if (!isAuthenticated && !isRequestFirstFlow) return null;

    if (!summary) {
        return (
            <div className="min-h-screen bg-[#F8F8F8] p-6">
                <div className="max-w-xl mx-auto bg-white rounded-xl shadow-sm p-8 text-center">
                    <p className="text-alsero-dark-gray mb-4">{t('reservation.no_summary')}</p>
                    <a href={resolveUrlFromMap(pageUrls, 'private-tours', language, 'en', '/private-tours')} className="text-alsero-first font-semibold hover:underline">
                        {t('reservation.browse_tours')}
                    </a>
                </div>
            </div>
        );
    }

    if (requestSubmitSuccess) {
        return (
            <div className="min-h-screen bg-[#F8F8F8] py-8 sm:py-12 flex items-center justify-center">
                <motion.div
                    className="max-w-xl mx-auto bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-alsero-dark-gray mb-2">
                        {t('request_form.success_message', language === 'tr' ? 'Talebiniz Alındı' : 'Request Received')}
                    </h2>
                    {requestNo && (
                        <p className="text-sm font-mono font-medium text-alsero-first mb-2">
                            {t('request_form.request_no_label', language === 'tr' ? 'Talep No' : 'Request No')}: {requestNo}
                        </p>
                    )}
                    <p className="text-alsero-dark-gray mb-6">
                        {t('request_form.success_detail', language === 'tr' ? 'Talebiniz başarıyla alındı. En kısa sürede size dönüş yapılacaktır.' : 'Your request has been received successfully. We will get back to you as soon as possible.')}
                    </p>
                    <a
                        href={resolveUrlFromMap(pageUrls, 'private-tours', language, 'en', '/private-tours')}
                        className="inline-block px-6 py-3 rounded-lg font-semibold text-white bg-alsero-first hover:bg-alsero-first/90 transition-colors"
                    >
                        {t('reservation.browse_tours')}
                    </a>
                </motion.div>
            </div>
        );
    }

    return (
        <motion.div
            className="min-h-screen bg-[#F8F8F8] py-8 sm:py-12"
            initial="hidden"
            animate="visible"
            variants={pageVariants}
        >
            <div className="container mx-auto px-4">
                <div className="grid grid-cols-12 gap-6 lg:gap-10">
                    {/* Sol: Form – kart bazlı */}
                    <motion.div
                        ref={formContainerRef}
                        className="col-span-12 lg:col-span-7 space-y-6"
                        variants={staggerContainerVariants}
                    >
                        <h1 className="text-2xl sm:text-3xl font-bold text-alsero-dark-gray">
                            {t('reservation.checkout_title')}
                        </h1>

                        <motion.form onSubmit={handleSubmit} className="space-y-6" variants={formItemVariants}>
                            {/* Bölüm 6: Misafir checkout için KVKK/GDPR uyarı metni */}
                            {isRequestFirstFlow && (
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                                    <p>{getVariable('checkout.kvkk_guest_account_notice', language === 'tr' ? 'Müşteri hesabınız KVKK, GDPR vb. kanunlara uygunluk için otomatik oluşturulacak ve şifreniz e-postanıza gönderilecektir. Şifrenizi güvenliğiniz için güncellemeyi unutmayın.' : 'Your customer account will be created automatically for compliance with KVKK, GDPR and similar regulations, and your password will be sent to your email. Please remember to update your password for security.')}</p>
                                </div>
                            )}
                            {/* Kart 1: İletişim kişisi bilgileri */}
                            {shouldShowContactForm ? (
                                <motion.section
                                    className="bg-white rounded-xl shadow-md border border-gray-200"
                                    variants={formItemVariants}
                                    {...motionItemProps}
                                >
                                    <div className="border-l-4 border-alsero-first bg-alsero-first/5 px-4 py-3">
                                        <h2 className="text-lg font-bold text-alsero-dark-gray">
                                            {t('reservation.contact_person_details') || (language === 'tr' ? 'İletişim kişisi bilgileri' : 'Contact person details')}
                                        </h2>
                                    </div>
                                    <div className="p-5 sm:p-6 space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="checkout-first-name" className="block font-semibold text-sm text-alsero-dark-gray mb-1.5">
                                                    {t('reservation.first_name')} <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    ref={firstInputRef}
                                                    id="checkout-first-name"
                                                    type="text"
                                                    value={form.first_name}
                                                    onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first text-alsero-dark-gray"
                                                    placeholder={t('reservation.first_name')}
                                                />
                                                {fieldErrors.first_name && (
                                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.first_name}</p>
                                                )}
                                            </div>
                                            <div>
                                                <label htmlFor="checkout-last-name" className="block font-semibold text-sm text-alsero-dark-gray mb-1.5">
                                                    {t('reservation.last_name')} <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="checkout-last-name"
                                                    type="text"
                                                    value={form.last_name}
                                                    onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first text-alsero-dark-gray"
                                                    placeholder={t('reservation.last_name')}
                                                />
                                                {fieldErrors.last_name && (
                                                    <p className="mt-1 text-sm text-red-600">{fieldErrors.last_name}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <label htmlFor="checkout-email" className="block font-semibold text-sm text-alsero-dark-gray mb-1.5">
                                                {t('reservation.email')} <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                id="checkout-email"
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first text-alsero-dark-gray"
                                                placeholder={t('reservation.email')}
                                            />
                                            {fieldErrors.email && (
                                                <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="checkout-phone" className="block font-semibold text-sm text-alsero-dark-gray mb-1.5">
                                                {t('reservation.phone')}
                                            </label>
                                            <input
                                                id="checkout-phone"
                                                type="tel"
                                                value={form.phone}
                                                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first text-alsero-dark-gray"
                                                placeholder={t('reservation.phone')}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                                            <div>
                                                <NationalitySelect
                                                    value={form.nationality}
                                                    onChange={(v) => setForm((f) => ({ ...f, nationality: v }))}
                                                    label={t('reservation.nationality')}
                                                />
                                            </div>
                                            {showTcForNationality(form.nationality) && (
                                                <div>
                                                    <label htmlFor="checkout-tc" className="block font-semibold text-sm text-alsero-dark-gray mb-1.5">
                                                        {t('reservation.national_id')}
                                                    </label>
                                                    <input
                                                        id="checkout-tc"
                                                        type="text"
                                                        inputMode="numeric"
                                                        maxLength={11}
                                                        value={form.national_id}
                                                        onChange={(e) => setForm((f) => ({ ...f, national_id: e.target.value.replace(/\D/g, '') }))}
                                                        onFocus={loadSavedTravelersOnce}
                                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first text-alsero-dark-gray"
                                                        placeholder={t('reservation.tc_placeholder')}
                                                    />
                                                </div>
                                            )}
                                            <div className={showTcForNationality(form.nationality) ? 'sm:col-span-2' : ''}>
                                                <label htmlFor="checkout-passport" className="block font-semibold text-sm text-alsero-dark-gray mb-1.5">
                                                    {t('reservation.passport_no')}
                                                </label>
                                                <input
                                                    id="checkout-passport"
                                                    type="text"
                                                    value={form.passport_no}
                                                    onChange={(e) => setForm((f) => ({ ...f, passport_no: e.target.value }))}
                                                    onFocus={loadSavedTravelersOnce}
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-alsero-first/20 focus:border-alsero-first text-alsero-dark-gray"
                                                    placeholder={t('reservation.passport_placeholder')}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </motion.section>
                            ) : (
                                <motion.section
                                    className="bg-white rounded-xl shadow-md border border-gray-200"
                                    variants={formItemVariants}
                                    {...motionItemProps}
                                >
                                    <div className="border-l-4 border-emerald-500 bg-emerald-50 px-4 py-3">
                                        <h2 className="text-lg font-bold text-alsero-dark-gray">
                                            {t('checkout.member_checkout')}
                                        </h2>
                                    </div>
                                    <div className="p-5 sm:p-6 text-sm text-gray-700 space-y-1">
                                        <p>{t('checkout.member_info_used')}</p>
                                        {customer?.email ? <p className="font-medium text-alsero-dark-gray">{customer.email}</p> : null}
                                    </div>
                                </motion.section>
                            )}

                            {/* Kart 2, 3, …: Oda X yolcu bilgileri */}
                            {participantsByRoom.length > 0 && participantsByRoom.map((travelers, roomIndex) => (
                                <motion.section
                                    key={roomIndex}
                                    className="bg-white rounded-xl shadow-md border border-gray-200"
                                    variants={formItemVariants}
                                    {...motionItemProps}
                                >
                                    <div className="border-l-4 border-alsero-first bg-alsero-first/5 px-4 py-3">
                                        <h2 className="text-lg font-bold text-alsero-dark-gray">
                                            {summary?.pricing_period_type === 'daily'
                                                ? t('reservation.guest_details', language === 'tr' ? 'Katılımcı bilgileri' : 'Participant details')
                                                : <>{t('reservation.room_number')} {roomIndex + 1} – {t('reservation.room_guest_details')}</>}
                                        </h2>
                                    </div>
                                    <div className="p-5 sm:p-6 space-y-5">
                                        {travelers.map((p, guestIndex) => (
                                            <div
                                                key={guestIndex}
                                                className="rounded-lg border border-gray-100 bg-gray-50/50 p-4 space-y-3"
                                            >
                                                <p className="text-sm font-semibold text-alsero-dark-gray border-b border-gray-200 pb-2 mb-2">
                                                    {p.type === 'adult' ? t('reservation.adult') : t('reservation.child')} {guestIndex + 1}
                                                </p>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                            {t('reservation.first_name')} <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={p.first_name}
                                                            onChange={(e) => {
                                                                setParticipantsByRoom((prev) =>
                                                                    prev.map((r, ri) =>
                                                                        ri === roomIndex ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, first_name: e.target.value } : trav)) : r
                                                                    )
                                                                );
                                                            }}
                                                            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-alsero-first/20 text-alsero-dark-gray ${fieldErrors[participantFieldKey(roomIndex, guestIndex, 'first_name')] ? 'border-red-500' : 'border-gray-300'}`}
                                                            placeholder={t('reservation.first_name')}
                                                        />
                                                        {fieldErrors[participantFieldKey(roomIndex, guestIndex, 'first_name')] && (
                                                            <p className="mt-1 text-xs text-red-600">{fieldErrors[participantFieldKey(roomIndex, guestIndex, 'first_name')]}</p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                            {t('reservation.last_name')} <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={p.last_name}
                                                            onChange={(e) => {
                                                                setParticipantsByRoom((prev) =>
                                                                    prev.map((r, ri) =>
                                                                        ri === roomIndex ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, last_name: e.target.value } : trav)) : r
                                                                    )
                                                                );
                                                            }}
                                                            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-alsero-first/20 text-alsero-dark-gray ${fieldErrors[participantFieldKey(roomIndex, guestIndex, 'last_name')] ? 'border-red-500' : 'border-gray-300'}`}
                                                            placeholder={t('reservation.last_name')}
                                                        />
                                                        {fieldErrors[participantFieldKey(roomIndex, guestIndex, 'last_name')] && (
                                                            <p className="mt-1 text-xs text-red-600">{fieldErrors[participantFieldKey(roomIndex, guestIndex, 'last_name')]}</p>
                                                        )}
                                                    </div>
                                                    <div className="sm:col-span-2 lg:col-span-1">
                                                        <NationalitySelect
                                                            value={p.nationality}
                                                            onChange={(v) => {
                                                                setParticipantsByRoom((prev) =>
                                                                    prev.map((r, ri) =>
                                                                        ri === roomIndex ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, nationality: v } : trav)) : r
                                                                    )
                                                                );
                                                            }}
                                                            label={`${t('reservation.nationality')} *`}
                                                        />
                                                        {fieldErrors[participantFieldKey(roomIndex, guestIndex, 'nationality')] && (
                                                            <p className="mt-1 text-xs text-red-600">{fieldErrors[participantFieldKey(roomIndex, guestIndex, 'nationality')]}</p>
                                                        )}
                                                    </div>
                                                    {showTcForNationality(p.nationality) && (
                                                        <div>
                                                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                                {t('reservation.national_id')} <span className="text-red-500">*</span>
                                                            </label>
                                                            <input
                                                                type="text"
                                                                inputMode="numeric"
                                                                maxLength={11}
                                                                value={p.national_id}
                                                                onChange={(e) => {
                                                                    setParticipantsByRoom((prev) =>
                                                                        prev.map((r, ri) =>
                                                                            ri === roomIndex
                                                                                ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, national_id: e.target.value.replace(/\D/g, '') } : trav))
                                                                                : r
                                                                        )
                                                                    );
                                                                }}
                                                                onFocus={loadSavedTravelersOnce}
                                                                onBlur={() => fillGuestFromSavedTraveler(roomIndex, guestIndex, p.passport_no, p.national_id)}
                                                                className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-alsero-first/20 text-alsero-dark-gray ${fieldErrors[participantFieldKey(roomIndex, guestIndex, 'national_id')] ? 'border-red-500' : 'border-gray-300'}`}
                                                                placeholder={t('reservation.tc_placeholder')}
                                                            />
                                                            {fieldErrors[participantFieldKey(roomIndex, guestIndex, 'national_id')] && (
                                                                <p className="mt-1 text-xs text-red-600">{fieldErrors[participantFieldKey(roomIndex, guestIndex, 'national_id')]}</p>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                            {t('reservation.passport_no')}{!showTcForNationality(p.nationality) && p.nationality ? <span className="text-red-500"> *</span> : null}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={p.passport_no}
                                                            onChange={(e) => {
                                                                setParticipantsByRoom((prev) =>
                                                                    prev.map((r, ri) =>
                                                                        ri === roomIndex ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, passport_no: e.target.value } : trav)) : r
                                                                    )
                                                                );
                                                            }}
                                                            onFocus={loadSavedTravelersOnce}
                                                            onBlur={() => fillGuestFromSavedTraveler(roomIndex, guestIndex, p.passport_no, p.national_id)}
                                                            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-alsero-first/20 text-alsero-dark-gray ${fieldErrors[participantFieldKey(roomIndex, guestIndex, 'passport_no')] ? 'border-red-500' : 'border-gray-300'}`}
                                                            placeholder={t('reservation.passport_placeholder')}
                                                        />
                                                        {fieldErrors[participantFieldKey(roomIndex, guestIndex, 'passport_no')] && (
                                                            <p className="mt-1 text-xs text-red-600">{fieldErrors[participantFieldKey(roomIndex, guestIndex, 'passport_no')]}</p>
                                                        )}
                                                    </div>
                                                    {p.type === 'child' && summary?.rooms?.[roomIndex] && (() => {
                                                        const room = summary.rooms[roomIndex];
                                                        const adults = room?.adults ?? 0;
                                                        const childIndexInRoom = guestIndex - adults;
                                                        const childAges = room?.childAges ?? [];
                                                        const age = childIndexInRoom >= 0 && childIndexInRoom < childAges.length ? childAges[childIndexInRoom] : 0;
                                                        const travelDate = summary?.travel_date || summary?.check_in_date;
                                                        let minDate = '';
                                                        let maxDate = '';
                                                        if (travelDate) {
                                                            const t = new Date(travelDate);
                                                            const max = new Date(t);
                                                            max.setFullYear(max.getFullYear() - age);
                                                            const min = new Date(max);
                                                            min.setFullYear(min.getFullYear() - 1);
                                                            min.setDate(min.getDate() + 1);
                                                            minDate = min.toISOString().slice(0, 10);
                                                            maxDate = max.toISOString().slice(0, 10);
                                                        }
                                                        return (
                                                            <div>
                                                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                                                    {t('reservation.child_birthdate')} <span className="text-red-500">*</span>
                                                                </label>
                                                                <input
                                                                    type="date"
                                                                    value={p.birth_date || ''}
                                                                    min={minDate || undefined}
                                                                    max={maxDate || undefined}
                                                                    onChange={(e) => {
                                                                        setParticipantsByRoom((prev) =>
                                                                            prev.map((r, ri) =>
                                                                                ri === roomIndex ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, birth_date: e.target.value } : trav)) : r
                                                                            )
                                                                        );
                                                                    }}
                                                                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-alsero-first/20 text-alsero-dark-gray ${fieldErrors[participantFieldKey(roomIndex, guestIndex, 'birth_date')] ? 'border-red-500' : 'border-gray-300'}`}
                                                                />
                                                                {fieldErrors[participantFieldKey(roomIndex, guestIndex, 'birth_date')] && (
                                                                    <p className="mt-1 text-xs text-red-600">{fieldErrors[participantFieldKey(roomIndex, guestIndex, 'birth_date')]}</p>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                    {isAuthenticated && (
                                                    <div className="sm:col-span-2 lg:col-span-3 flex items-center">
                                                        <label className="inline-flex items-center gap-2 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!p.save_for_future}
                                                                onChange={(e) => {
                                                                    setParticipantsByRoom((prev) =>
                                                                        prev.map((r, ri) =>
                                                                            ri === roomIndex ? r.map((trav, ti) => (ti === guestIndex ? { ...trav, save_for_future: e.target.checked } : trav)) : r
                                                                        )
                                                                    );
                                                                }}
                                                                className="rounded border-gray-300 text-alsero-first focus:ring-alsero-first/20"
                                                            />
                                                            <span className="text-xs font-medium text-gray-700">
                                                                {t('reservation.save_for_future')}
                                                            </span>
                                                        </label>
                                                    </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </motion.section>
                            ))}

                            {isCaptchaRequired && (captchaConfig?.provider === 'google_recaptcha_v2' || captchaConfig?.provider === 'cloudflare_turnstile') && (
                                <div id="checkout-request-captcha" className="min-h-[78px]" />
                            )}

                            {error && (
                                <div className="p-3 rounded-xl bg-red-50 border border-red-200">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            )}

                            <motion.button
                                type="submit"
                                disabled={submitting || captchaConfigLoading}
                                className="w-full py-3.5 px-6 rounded-xl font-semibold text-white bg-alsero-first hover:bg-alsero-first/90 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-md"
                                variants={formItemVariants}
                            >
                                {submitting ? t('reservation.submitting') : isRequestFirstFlow
                                    ? t('request_form.submit', 'Talebi Gönder')
                                    : t('reservation.proceed_to_payment')}
                            </motion.button>
                        </motion.form>
                    </motion.div>

                    {/* Sağ: Tur ve fiyat özeti */}
                    <motion.aside
                        className="col-span-12 lg:col-span-5"
                        variants={slideFromRightVariants}
                    >
                        <div className="lg:sticky lg:top-8 bg-white rounded-xl shadow-sm border border-gray-200/80 p-6">
                            <h2 className="text-xl font-bold text-alsero-dark-gray mb-4">
                                {t('reservation.tour_summary')}
                            </h2>
                            <p className="font-medium text-alsero-dark-gray mb-2">{summary.product_name}</p>
                            {summary.package_name && (
                                <p className="text-sm text-alsero-first font-medium mb-1">
                                    {t('reservation.package', 'Paket')}: {(summary.package_name as string).toUpperCase()}
                                </p>
                            )}
                            {summary.travel_date && (
                                <p className="text-sm text-gray-600 mb-1">
                                    {t('reservation.date')}: {summary.travel_date}
                                </p>
                            )}
                            {summary.rooms && summary.rooms.length > 0 ? (
                                <div className="text-sm text-gray-600 space-y-1 mb-3">
                                    {summary.rooms.map((room, idx) => {
                                        const adults = room.adults ?? 0;
                                        const children = room.children ?? 0;
                                        const ages = room.childAges ?? [];
                                        const parts: string[] = [];
                                        if (adults > 0) {
                                            parts.push(`${adults} ${adults === 1 ? t('reservation.adult') : t('reservation.adults')}`);
                                        }
                                        if (children > 0) {
                                            const ageStr = ages.length > 0 ? ` (${ages.join(', ')} ${t('reservation.years_old')})` : '';
                                            parts.push(`${children} ${children === 1 ? t('reservation.child') : t('reservation.children')}${ageStr}`);
                                        }
                                        return (
                                            <p key={idx}>
                                                {summary.pricing_period_type !== 'daily' && <>{t('reservation.room_number')} {idx + 1}: </>}{parts.length > 0 ? parts.join(', ') : '-'}
                                            </p>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-600 mb-3">
                                    {t('reservation.guest_count')}: {summary.guest_count ?? 0}
                                    {summary.child_count ? `, ${t('reservation.child')}: ${summary.child_count}` : ''}
                                </p>
                            )}
                            {summary.total_price != null && summary.currency && (
                                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                                    {summary.coupon_code && (summary.coupon_discount_amount ?? 0) > 0 && (
                                        <p className="text-sm text-gray-600">
                                            {t('reservation.coupon_applied') || (language === 'tr' ? 'Kupon indirimi' : 'Coupon discount')}: -{(summary.coupon_discount_amount ?? 0).toFixed(2)} {summary.currency.code || summary.currency.symbol}
                                        </p>
                                    )}
                                    <p className="text-alsero-first font-bold text-lg">
                                        {t('reservation.total_price')}: {summary.total_price.toFixed(2)} {summary.currency.code || summary.currency.symbol}
                                    </p>
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={scrollToFormAndFocus}
                                className="mt-4 w-full py-2.5 px-4 rounded-lg font-medium text-alsero-first border-2 border-alsero-first hover:bg-alsero-first hover:text-white transition-colors"
                            >
                                {t('reservation.edit_reservation') || (language === 'tr' ? 'Rezervasyonu tekrar düzenle' : 'Edit reservation')}
                            </button>
                        </div>
                    </motion.aside>
                </div>
            </div>
        </motion.div>
    );
}
